import { useRouter } from 'next/router';
import { useContext } from 'react';
import { CategoryTitle } from 'src/components/shared/category/CategoryRanking/CategoryTitle';
import { RankingList } from 'src/components/shared/category/CategoryRanking/RankingList';
import { SkeletonList } from 'src/components/shared/category/CategoryRanking/SkeletonList';
import { BaseButton } from 'src/components/shared/elements/buttons/BaseButton';
import { KaimonoWebRoutes } from 'src/config/routes';
import { useProductsByRanking } from 'src/hooks/useProducts';
import { I18nContext } from 'src/stores/I18n';
import { mixpanel } from 'src/utils/mixpanel';
import styles from './index.module.scss';

const PRODUCT_COUNT_LIMIT = 20;

type Props = {
  categoryName: string;
  categorySlug: string;
};

export const CategoryRanking: React.FC<Props> = ({ categoryName, categorySlug }) => {
  const router = useRouter();
  const { products, isProductsLoading: isLoading } = useProductsByRanking(
    { slug: categorySlug, type: getCategoryType(router.asPath) },
    PRODUCT_COUNT_LIMIT
  );

  const localeResources = useContext(I18nContext);
  const hasProducts = !!products && products.length > 0;
  const handleClick = (largeCategorySlug: string) => {
    mixpanel.track('RankingSectionClicked', {
      rankingType: largeCategorySlug,
      to: KaimonoWebRoutes.ranking.index
    });
    router.push(`${KaimonoWebRoutes.ranking.index}?slug=${largeCategorySlug}`);
  };
  const isFromLargeCategoryPage = router.asPath.includes('large_categories');

  return (
    <section className={styles.container}>
      {hasProducts && <CategoryTitle categoryName={categoryName} />}
      {isLoading && <SkeletonList count={20} />}
      {hasProducts && <RankingList products={products} categorySlug={categorySlug} />}
      {categorySlug && isFromLargeCategoryPage && (
        <div className={styles.buttonContainer}>
          <BaseButton
            text={localeResources.commons.showMore}
            paddingX={16}
            isBlack
            outline
            hasArrowRight
            width={280}
            onClick={() => handleClick(categorySlug)}
          />
        </div>
      )}
    </section>
  );
};

const getCategoryType = (path: string) => {
  const category = path.split('/')[1];
  switch (category) {
    case 'large_categories':
      return 'large';
    case 'middle_categories':
      return 'middle';
    case 'small_categories':
      return 'small';
    default:
      return 'all';
  }
};
